import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { controllerFullName, ensureControllerNames, useControllerNames } from '../../services/controllerNames';
import {
  hasActiveQuickFilters,
  NONE_KEY,
  QUICK_FILTER_DIMS,
  type Facet,
  type QuickFacets,
  type QuickFilterDim,
  type QuickFilters,
} from './quickFilters';
import './QuickFilterBar.css';

// Con más opciones que esto, la hoja trae un buscador (un admin puede tener
// quince fábricas en un día).
const SHEET_SEARCH_THRESHOLD = 8;

const DIM_KEYS: Record<QuickFilterDim, { label: string; title: string; none: string }> = {
  controller: { label: 'controller', title: 'titleController', none: 'noneController' },
  factory: { label: 'factory', title: 'titleFactory', none: 'noneFactory' },
  carrier: { label: 'carrier', title: 'titleCarrier', none: 'noneCarrier' },
};

interface Props {
  facets: QuickFacets;
  filters: QuickFilters;
  onChange: (next: QuickFilters) => void;
  /** Cargas que se ven con todos los filtros (también el buscador). */
  shown: number;
  /** Cargas del día antes de filtrar (con el interruptor self-managed ya aplicado). */
  total: number;
}

/**
 * Pastillas de filtro rápido bajo la barra de fecha (oct 2026). Una por
 * dimensión con más de un valor en el día; al tocarla se abre una hoja con los
 * valores y cuántas cargas tiene cada uno. "Pendientes" se activa de un toque.
 * Si no hay nada que filtrar, no ocupa sitio.
 */
export function QuickFilterBar({ facets, filters, onChange, shown, total }: Props) {
  const { t } = useTranslation();
  const [openDim, setOpenDim] = useState<QuickFilterDim | null>(null);
  const tq = (k: string, o?: Record<string, unknown>) => t(`positions.quickFilters.${k}`, o);
  const [row, setRow] = useState<HTMLDivElement | null>(null);

  // En escritorio la rueda vertical desplaza la fila en horizontal mientras le
  // quede recorrido; en los extremos deja pasar el scroll a la página. Listener
  // propio (no onWheel de React, que es pasivo y no deja cancelar el scroll).
  useEffect(() => {
    if (!row) return;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0 || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      const max = row.scrollWidth - row.clientWidth;
      if (max <= 0) return;
      const atStart = row.scrollLeft <= 0 && e.deltaY < 0;
      const atEnd = row.scrollLeft >= max - 1 && e.deltaY > 0;
      if (atStart || atEnd) return;
      e.preventDefault();
      row.scrollLeft += e.deltaY;
    };
    row.addEventListener('wheel', onWheel, { passive: false });
    return () => row.removeEventListener('wheel', onWheel);
  }, [row]);

  // Nombres de los controladores (la tarjeta los pide igual). Re-render cuando lleguen.
  useControllerNames();
  useEffect(() => {
    if (facets.controller.visible) void ensureControllerNames();
  }, [facets.controller.visible]);

  const active = hasActiveQuickFilters(filters);
  const visibleDims = QUICK_FILTER_DIMS.filter((d) => facets[d].visible);
  if (!facets.pending.visible && visibleDims.length === 0 && !active) return null;

  const optionLabel = (dim: QuickFilterDim, key: string, raw: string): string => {
    if (key === NONE_KEY) return tq(DIM_KEYS[dim].none);
    if (dim === 'controller') return controllerFullName(raw) ?? raw;
    return raw;
  };

  const pillText = (facet: Facet): string => {
    const base = tq(DIM_KEYS[facet.dim].label);
    const sel = filters[facet.dim];
    if (sel.length === 0) return base;
    if (sel.length === 1) {
      const opt = facet.options.find((o) => o.key === sel[0]);
      return opt ? optionLabel(facet.dim, opt.key, opt.label) : base;
    }
    return `${base} · ${sel.length}`;
  };

  const togglePending = () => onChange({ ...filters, pending: !filters.pending });

  return (
    <div className="quick-filters">
      <div ref={setRow} className="quick-filters__row" role="toolbar" aria-label={tq('aria')}>
        {facets.pending.visible && (
          <button
            type="button"
            className={`qf-pill${filters.pending ? ' qf-pill--active' : ''}`}
            aria-pressed={filters.pending}
            onClick={togglePending}
          >
            {filters.pending && <CheckIcon />}
            <span className="qf-pill__text">{tq('pending')}</span>
            <span className="qf-pill__count">{facets.pending.pendingCount}</span>
          </button>
        )}
        {visibleDims.map((dim) => {
          const facet = facets[dim];
          const on = filters[dim].length > 0;
          return (
            <button
              key={dim}
              type="button"
              className={`qf-pill${on ? ' qf-pill--active' : ''}`}
              aria-haspopup="dialog"
              aria-expanded={openDim === dim}
              onClick={() => setOpenDim(dim)}
            >
              <DimIcon dim={dim} />
              <span className="qf-pill__text">{pillText(facet)}</span>
              <svg className="qf-pill__chevron" viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </button>
          );
        })}
        {active && (
          <button
            type="button"
            className="qf-pill qf-pill--clear"
            onClick={() => onChange({ controller: [], factory: [], carrier: [], pending: false })}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
            <span className="qf-pill__text">{tq('clearAll')}</span>
          </button>
        )}
      </div>
      {active && (
        <div className="quick-filters__count" role="status" aria-live="polite">
          {tq('count', { shown, total })}
        </div>
      )}

      {openDim && (
        <FilterSheet
          facet={facets[openDim]}
          title={tq(DIM_KEYS[openDim].title)}
          selected={filters[openDim]}
          shown={shown}
          labelOf={(key, raw) => optionLabel(openDim, key, raw)}
          secondaryOf={openDim === 'controller' ? (key, raw) => (key !== NONE_KEY && controllerFullName(raw) ? raw : undefined) : undefined}
          onToggle={(key) => {
            const cur = filters[openDim];
            const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
            onChange({ ...filters, [openDim]: next });
          }}
          onClear={() => onChange({ ...filters, [openDim]: [] })}
          onClose={() => setOpenDim(null)}
        />
      )}
    </div>
  );
}

function FilterSheet({
  facet,
  title,
  selected,
  shown,
  labelOf,
  secondaryOf,
  onToggle,
  onClear,
  onClose,
}: {
  facet: Facet;
  title: string;
  selected: string[];
  shown: number;
  labelOf: (key: string, raw: string) => string;
  secondaryOf?: (key: string, raw: string) => string | undefined;
  onToggle: (key: string) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const tq = (k: string, o?: Record<string, unknown>) => t(`positions.quickFilters.${k}`, o);
  const [query, setQuery] = useState('');
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // Escape cierra; el foco entra en la hoja para quien navega con teclado.
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const options = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    if (!q) return facet.options;
    return facet.options.filter((o) => {
      const text = `${labelOf(o.key, o.label)} ${o.label}`.toLocaleLowerCase();
      return text.includes(q);
    });
  }, [facet.options, query, labelOf]);

  // Portal a <body>: la barra vive dentro del bloque fijo de la lista (z-index
  // 99), y una hoja renderizada ahí quedaría DEBAJO de la barra inferior y del
  // botón (+), por mucho z-index que tenga.
  return createPortal(
    <div className="qf-sheet-overlay" onClick={onClose}>
      <div className="qf-sheet" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="qf-sheet__grip" aria-hidden="true" />
        <div className="qf-sheet__header">
          <span className="qf-sheet__title">{title}</span>
          <button ref={closeRef} type="button" className="qf-sheet__close" onClick={onClose} aria-label={tq('close')}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        {facet.options.length > SHEET_SEARCH_THRESHOLD && (
          <input
            type="text"
            className="qf-sheet__search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={tq('search')}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        )}

        <ul className="qf-sheet__list">
          {options.map((o) => {
            const checked = selected.includes(o.key);
            // Con los demás filtros puestos, esta opción no dejaría nada: se
            // enseña apagada (no desaparece, para que la lista no baile).
            const dead = o.count === 0 && !checked;
            const secondary = secondaryOf?.(o.key, o.label);
            return (
              <li key={o.key}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={checked}
                  disabled={dead}
                  className={`qf-option${checked ? ' qf-option--checked' : ''}${o.key === NONE_KEY ? ' qf-option--none' : ''}`}
                  onClick={() => onToggle(o.key)}
                >
                  <span className="qf-option__box" aria-hidden="true">{checked && <CheckIcon />}</span>
                  <span className="qf-option__label">
                    <span className="qf-option__main">{labelOf(o.key, o.label)}</span>
                    {secondary && <span className="qf-option__secondary">{secondary}</span>}
                  </span>
                  <span className="qf-option__count">{o.count}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="qf-sheet__footer">
          <button type="button" className="qf-sheet__clear" onClick={onClear} disabled={selected.length === 0}>
            {tq('clearDim')}
          </button>
          <button type="button" className="qf-sheet__apply" onClick={onClose}>
            {tq('show', { count: shown })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 10.5l4 4 8-9" />
    </svg>
  );
}

function DimIcon({ dim }: { dim: QuickFilterDim }) {
  const path = {
    controller: 'M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z',
    factory: 'M22 22H2V10l7-4v4l7-4v4h2V2h4v20zM6 18h2v-3H6v3zm5 0h2v-3h-2v3zm5 0h2v-3h-2v3z',
    carrier: 'M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9 1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z',
  }[dim];
  return (
    <svg className="qf-pill__icon" viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}
